import { useState, useRef } from 'react';

export default function ContactForm() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormState('success');
      return;
    }

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setErrorMsg('留言内容不能超过500个字符');
      setFormState('error');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9ooiaiqs38sj6jc7cl0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const displayMsg = typeof serverMsg === 'string' && serverMsg.includes('spam')
          ? '提交失败，请稍后重试'
          : (typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        setErrorMsg(displayMsg);
        setFormState('error');
      }
    } catch {
      setErrorMsg('网络错误，请检查连接后重试');
      setFormState('error');
    }
  };

  return (
    <div className="bg-background-50 rounded-xl border border-background-200/60 p-6 md:p-8 max-w-[600px] mx-auto">
      <h3 className="text-lg font-heading text-foreground-950 mb-2 text-center">提交反馈</h3>
      <p className="text-xs text-foreground-500 text-center mb-6">你的每一条建议都是我们前进的动力</p>

      {formState === 'success' ? (
        <div className="text-center py-8">
          <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-2xl text-primary-500"></i>
          </div>
          <p className="text-foreground-900 font-medium">感谢你的反馈！</p>
          <p className="text-sm text-foreground-500 mt-1">我们会认真阅读每一条建议</p>
          <button
            onClick={() => setFormState('idle')}
            className="mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
          >
            继续提交
          </button>
        </div>
      ) : (
        <form ref={formRef} onSubmit={handleSubmit} data-readdy-form>
          <div className="space-y-4">
            <div>
              <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                className="w-full px-4 py-2.5 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                placeholder="请输入你的姓名"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                className="w-full px-4 py-2.5 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                placeholder="请输入你的邮箱地址"
              />
            </div>
            <div>
              <label htmlFor="contact-category" className="block text-xs font-medium text-foreground-700 mb-1.5">反馈类型</label>
              <select
                id="contact-category"
                name="category"
                className="w-full px-4 py-2.5 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors cursor-pointer"
              >
                <option value="suggestion">功能建议</option>
                <option value="bug">报告问题</option>
                <option value="content">内容反馈</option>
                <option value="other">其他</option>
              </select>
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-2.5 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                placeholder="请详细描述你的建议或遇到的问题（不超过500字）"
              />
              <p className="text-xs text-foreground-400 mt-1 text-right">最多500个字符</p>
            </div>

            {/* Honeypot */}
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              readOnly
              aria-hidden="true"
              className="honeypot-field"
            />

            {formState === 'error' && (
              <div className="flex items-center gap-2 p-3 bg-primary-50 text-primary-700 rounded-lg text-xs">
                <i className="ri-error-warning-line"></i>
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              提交反馈
            </button>
          </div>
        </form>
      )}
    </div>
  );
}